/**
 * HOA Title Company Portal (Phase 2) — register, login, dashboard, new request.
 * Routes:
 *   /title/register    -> TitleRegisterPage
 *   /title/login       -> TitleLoginPage
 *   /title/dashboard   -> TitleDashboardPage (protected)
 *   /title/request     -> TitleNewRequestPage (protected)
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, LogIn, LogOut, FileText, Download, Plus, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import hoaApi, { formatApiErrorDetail } from '@/utils/hoaApi';

const GOLD = '#BCA480';
const DARK = '#434345';
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// -----------------------------------------------------------------------------
// Separate auth context (own localStorage key so it doesn't collide with homeowner / admin)
// -----------------------------------------------------------------------------

const TITLE_TOKEN_KEY = 'title_token';
const TITLE_USER_KEY = 'title_user';
const TitleAuthContext = createContext(null);

export const TitleAuthProvider = ({ children }) => {
  const [titleCo, setTitleCo] = useState(null);  // null = checking, false = unauth, obj = authed

  useEffect(() => {
    const t = localStorage.getItem(TITLE_TOKEN_KEY);
    const u = localStorage.getItem(TITLE_USER_KEY);
    if (t && u) {
      // Attach token to the hoaApi for this request only — then route-level checks work
      hoaApi.get('/hoa/title/me', { headers: { Authorization: `Bearer ${t}` } })
        .then(({ data }) => { setTitleCo(data); localStorage.setItem(TITLE_USER_KEY, JSON.stringify(data)); })
        .catch(() => { localStorage.removeItem(TITLE_TOKEN_KEY); localStorage.removeItem(TITLE_USER_KEY); setTitleCo(false); });
    } else {
      setTitleCo(false);
    }
  }, []);

  const signIn = (token, user) => {
    localStorage.setItem(TITLE_TOKEN_KEY, token);
    localStorage.setItem(TITLE_USER_KEY, JSON.stringify(user));
    setTitleCo(user);
  };
  const signOut = () => {
    localStorage.removeItem(TITLE_TOKEN_KEY);
    localStorage.removeItem(TITLE_USER_KEY);
    setTitleCo(false);
  };
  return <TitleAuthContext.Provider value={{ titleCo, signIn, signOut }}>{children}</TitleAuthContext.Provider>;
};

const useTitleAuth = () => {
  const ctx = useContext(TitleAuthContext);
  if (!ctx) throw new Error('useTitleAuth outside provider');
  return ctx;
};

// Wrapper that attaches the title token for API calls on this flow
const withTitleAuth = (config = {}) => {
  const t = localStorage.getItem(TITLE_TOKEN_KEY);
  return { ...config, headers: { ...(config.headers || {}), ...(t ? { Authorization: `Bearer ${t}` } : {}) } };
};

// -----------------------------------------------------------------------------
// Shared chrome
// -----------------------------------------------------------------------------

const AuthShell = ({ title, subtitle, children, footer }) => (
  <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ backgroundColor: '#f4efe6' }}>
    <div className="w-full max-w-md">
      <Link to="/" className="flex items-center justify-center mb-8">
        <div className="text-center">
          <div className="text-xs tracking-[0.3em] font-bold" style={{ color: GOLD }}>BESPOKE REALTY</div>
          <div className="text-[11px] uppercase tracking-widest text-gray-500 mt-0.5">Title Company Portal</div>
        </div>
      </Link>
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-2xl font-bold mb-1" style={{ color: DARK }}>{title}</h1>
        {subtitle && <p className="text-sm text-gray-600 mb-6">{subtitle}</p>}
        {children}
      </div>
      {footer && <div className="text-center mt-5 text-sm text-gray-600">{footer}</div>}
    </div>
  </div>
);

// -----------------------------------------------------------------------------
// LOGIN
// -----------------------------------------------------------------------------

export const TitleLoginPage = () => {
  const { titleCo, signIn } = useTitleAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (titleCo) return <Navigate to="/title/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await hoaApi.post('/hoa/title/login', { email, password });
      signIn(data.access_token, data.title_company);
      navigate('/title/dashboard');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Login failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Sign in to request a payoff"
      subtitle="For title companies and closing attorneys."
      footer={<Link to="/title/register" className="hover:underline" style={{ color: GOLD }}>Need an account? Register →</Link>}
    >
      <form onSubmit={submit} className="space-y-4">
        <div><Label>Email</Label><Input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="title-login-email" /></div>
        <div><Label>Password</Label><Input type="password" required value={password} onChange={(e)=>setPassword(e.target.value)} data-testid="title-login-password" /></div>
        <Button type="submit" className="w-full text-white" style={{ backgroundColor: GOLD }} disabled={submitting} data-testid="title-login-submit">
          {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin"/> Signing in…</> : <><LogIn className="h-4 w-4 mr-2"/> Sign In</>}
        </Button>
      </form>
    </AuthShell>
  );
};

// -----------------------------------------------------------------------------
// REGISTER
// -----------------------------------------------------------------------------

export const TitleRegisterPage = () => {
  const { signIn, titleCo } = useTitleAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ firm_name: '', name: '', email: '', phone: '', bar_license: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  if (titleCo) return <Navigate to="/title/dashboard" replace />;
  const upd = (k, v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) { toast.error('Password must be at least 8 characters.'); return; }
    setSubmitting(true);
    try {
      const { data } = await hoaApi.post('/hoa/title/register', f);
      signIn(data.access_token, data.title_company);
      toast.success('Account created. Welcome.');
      navigate('/title/dashboard');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Create your Title Portal account"
      subtitle="Self-registration for licensed title companies and closing attorneys."
      footer={<Link to="/title/login" className="hover:underline" style={{ color: GOLD }}>Already have an account? Sign in →</Link>}
    >
      <form onSubmit={submit} className="space-y-3">
        <div><Label>Firm / Company Name *</Label><Input required value={f.firm_name} onChange={(e)=>upd('firm_name', e.target.value)} data-testid="title-reg-firm" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Your Name *</Label><Input required value={f.name} onChange={(e)=>upd('name', e.target.value)} data-testid="title-reg-name" /></div>
          <div><Label>Phone *</Label><Input required value={f.phone} onChange={(e)=>upd('phone', e.target.value)} data-testid="title-reg-phone" /></div>
        </div>
        <div><Label>Email *</Label><Input required type="email" value={f.email} onChange={(e)=>upd('email', e.target.value)} data-testid="title-reg-email" /></div>
        <div><Label>Bar / License # (optional)</Label><Input value={f.bar_license} onChange={(e)=>upd('bar_license', e.target.value)} data-testid="title-reg-bar" /></div>
        <div><Label>Password *</Label><Input required type="password" minLength={8} value={f.password} onChange={(e)=>upd('password', e.target.value)} data-testid="title-reg-password" /></div>
        <Button type="submit" className="w-full text-white" style={{ backgroundColor: GOLD }} disabled={submitting} data-testid="title-reg-submit">
          {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin"/> Creating…</> : 'Create Account'}
        </Button>
      </form>
    </AuthShell>
  );
};

// -----------------------------------------------------------------------------
// DASHBOARD
// -----------------------------------------------------------------------------

const StatusBadge = ({ status }) => {
  const map = {
    pending_payment: { text: 'Payment Pending', bg: 'bg-amber-100', fg: 'text-amber-800', icon: Clock },
    paid: { text: 'Paid — Awaiting Review', bg: 'bg-blue-100', fg: 'text-blue-800', icon: Clock },
    approved: { text: 'Approved', bg: 'bg-indigo-100', fg: 'text-indigo-800', icon: CheckCircle2 },
    sent: { text: 'Sent', bg: 'bg-green-100', fg: 'text-green-800', icon: CheckCircle2 },
    cancelled: { text: 'Cancelled', bg: 'bg-gray-100', fg: 'text-gray-600', icon: Clock },
  };
  const m = map[status] || { text: status, bg: 'bg-gray-100', fg: 'text-gray-600', icon: Clock };
  const Icon = m.icon;
  return <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-0.5 rounded ${m.bg} ${m.fg}`}><Icon className="h-3 w-3"/> {m.text}</span>;
};

const PortalHeader = ({ onSignOut }) => (
  <header className="border-b border-[#e6dec7] bg-white">
    <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
      <Link to="/title/dashboard">
        <div className="text-xs tracking-[0.3em] font-bold" style={{ color: GOLD }}>BESPOKE REALTY</div>
        <div className="text-[11px] uppercase tracking-widest text-gray-500 mt-0.5">Title Company Portal</div>
      </Link>
      <button onClick={onSignOut} className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#BCA480]" data-testid="title-signout-btn">
        <LogOut className="h-4 w-4"/> Sign Out
      </button>
    </div>
  </header>
);

export const TitleDashboardPage = () => {
  const { titleCo, signOut } = useTitleAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    if (titleCo === false) { navigate('/title/login', { replace: true }); return; }
    if (!titleCo) return;
    hoaApi.get('/hoa/title/estoppel-requests', withTitleAuth())
      .then((r) => setRequests(r.data))
      .catch(() => toast.error('Failed to load requests.'))
      .finally(() => setLoading(false));

    // Handle Stripe return params
    const params = new URLSearchParams(location.search);
    if (params.get('paid') === '1') {
      setBanner({ type: 'success', message: 'Payment received. Your request is now in review — you\'ll receive the signed letter by email within 1 business day.' });
      window.history.replaceState({}, '', '/title/dashboard');
    } else if (params.get('cancelled') === '1') {
      setBanner({ type: 'info', message: 'Payment cancelled. No charges were made.' });
      window.history.replaceState({}, '', '/title/dashboard');
    }
  }, [titleCo, navigate, location.search]);

  if (titleCo === null || loading) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f4efe6' }}><Loader2 className="h-10 w-10 animate-spin" style={{ color: GOLD }}/></div>;
  if (!titleCo) return null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f9f7f3' }} data-testid="title-dashboard-page">
      <PortalHeader onSignOut={() => { signOut(); navigate('/title/login'); }}/>
      <main className="max-w-6xl mx-auto px-6 py-12">
        {banner && <div className={`mb-6 p-4 rounded-lg border text-sm ${banner.type==='success'?'bg-green-50 border-green-200 text-green-800':'bg-blue-50 border-blue-200 text-blue-800'}`}>{banner.message}</div>}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: DARK }}>Welcome, {titleCo.name.split(' ')[0]}.</h1>
            <p className="text-gray-600 mt-1">{titleCo.firm_name}</p>
          </div>
          <Link to="/title/request" className="inline-flex items-center gap-2 text-white px-5 py-3 rounded font-semibold hover:opacity-90" style={{ backgroundColor: GOLD }} data-testid="title-new-request-btn">
            <Plus className="h-4 w-4"/> Request a Payoff
          </Link>
        </div>

        <div className="bg-white rounded-lg border border-[#e6dec7] overflow-hidden">
          <div className="px-6 py-4 border-b border-[#e6dec7] flex items-center gap-2">
            <FileText className="h-4 w-4" style={{ color: GOLD }}/>
            <h2 className="font-bold" style={{ color: DARK }}>Your Payoff Requests</h2>
            <span className="text-xs text-gray-500 ml-2">{requests.length}</span>
          </div>
          {requests.length === 0 ? (
            <div className="text-center text-gray-500 py-12 text-sm" data-testid="title-empty">
              No requests yet. Click "Request a Payoff" to start your first one.
            </div>
          ) : (
            <ul>
              {requests.map((r) => (
                <li key={r.id} className="px-6 py-4 border-t border-gray-100 first:border-t-0 flex items-center justify-between gap-4" data-testid={`title-req-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold" style={{ color: DARK }}>{r.property_address}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {r.association_name || 'Association TBD'} · Closing {r.closing_date} · {money(r.fee_amount)}{r.rush?' · RUSH':''}
                    </div>
                  </div>
                  <StatusBadge status={r.status}/>
                  {r.pdf_url && (
                    <a href={r.pdf_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: GOLD }}>
                      <Download className="h-4 w-4"/> PDF
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
};

// -----------------------------------------------------------------------------
// NEW REQUEST
// -----------------------------------------------------------------------------

export const TitleNewRequestPage = () => {
  const { titleCo, signOut } = useTitleAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ property_address: '', closing_date: '', buyer_name: '', seller_name: '', rush: false, notes: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (titleCo === false) navigate('/title/login', { replace: true });
  }, [titleCo, navigate]);

  if (!titleCo) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f4efe6' }}><Loader2 className="h-10 w-10 animate-spin" style={{ color: GOLD }}/></div>;

  const upd = (k, v) => setF({ ...f, [k]: v });
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await hoaApi.post('/hoa/title/estoppel-request', f, withTitleAuth());
      // Redirect to Stripe Checkout for the fee
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f9f7f3' }} data-testid="title-new-request-page">
      <PortalHeader onSignOut={() => { signOut(); navigate('/title/login'); }}/>
      <main className="max-w-2xl mx-auto px-6 py-12">
        <Link to="/title/dashboard" className="text-sm hover:underline" style={{ color: GOLD }}>← Back to dashboard</Link>
        <h1 className="text-3xl font-bold mt-4 mb-2" style={{ color: DARK }}>Request a Payoff / Estoppel Letter</h1>
        <p className="text-gray-600 mb-8">Standard turnaround is 1 business day. Rush requests ($100 surcharge) are prioritized same-day.</p>
        <form onSubmit={submit} className="bg-white rounded-lg shadow p-8 space-y-5" data-testid="title-new-request-form">
          <div><Label>Property Address *</Label><Input required value={f.property_address} onChange={(e)=>upd('property_address', e.target.value)} placeholder="101 Oak Grove Dr, Olive Branch, MS" data-testid="title-req-address"/></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Closing Date *</Label><Input required type="date" value={f.closing_date} onChange={(e)=>upd('closing_date', e.target.value)} data-testid="title-req-closing"/></div>
            <div>
              <Label>Request Speed</Label>
              <select value={f.rush ? 'rush' : 'standard'} onChange={(e)=>upd('rush', e.target.value==='rush')} className="w-full h-10 border border-gray-200 rounded px-3 text-sm" data-testid="title-req-rush">
                <option value="standard">Standard — 1 business day</option>
                <option value="rush">Rush (+$100) — same business day</option>
              </select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Seller Name *</Label><Input required value={f.seller_name} onChange={(e)=>upd('seller_name', e.target.value)} data-testid="title-req-seller"/></div>
            <div><Label>Buyer Name *</Label><Input required value={f.buyer_name} onChange={(e)=>upd('buyer_name', e.target.value)} data-testid="title-req-buyer"/></div>
          </div>
          <div><Label>Notes (optional)</Label><Textarea rows={3} value={f.notes} onChange={(e)=>upd('notes', e.target.value)} placeholder="Anything we should know?" data-testid="title-req-notes"/></div>
          <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Fee: <strong>{money(f.rush ? 300 : 200)}</strong> · Charged at submission via Stripe
            </div>
            <Button type="submit" className="text-white" style={{ backgroundColor: GOLD }} disabled={submitting} data-testid="title-req-submit">
              {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin"/> Starting payment…</> : 'Submit & Pay'}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};
